import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Check, X, RefreshCw, UserCheck } from 'lucide-react'
import { PendingUserDTO, userApprovalApi } from '../../services/api/userApproval'
import { formatDateTime } from '@/lib/utils'

type Action = { user: PendingUserDTO; kind: 'approve' | 'reject' } | null

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]!.toUpperCase()).join('') || '?'
}
function avatarColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return `bk-avatar-c${Math.abs(hash) % 6}`
}

export default function PendingUsersPage() {
  const [users, setUsers] = useState<PendingUserDTO[]>([])
  const [authorities, setAuthorities] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [action, setAction] = useState<Action>(null)

  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>([])
  const [rejectionReason, setRejectionReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const [list, auths] = await Promise.all([
        userApprovalApi.listPending(),
        userApprovalApi.authorities(),
      ])
      setUsers(list)
      setAuthorities(auths)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load pending users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void refresh() }, [])

  const openApprove = (u: PendingUserDTO) => { setSelectedAuthorities([]); setAction({ user: u, kind: 'approve' }) }
  const openReject  = (u: PendingUserDTO) => { setRejectionReason(''); setAction({ user: u, kind: 'reject' }) }
  const close       = () => setAction(null)

  const submit = async () => {
    if (!action) return
    setSubmitting(true)
    try {
      if (action.kind === 'approve') {
        if (selectedAuthorities.length === 0) { toast.error('Pick at least one role to approve'); setSubmitting(false); return }
        await userApprovalApi.approve(action.user.id, selectedAuthorities)
        toast.success(`Approved ${action.user.email || action.user.login}`)
      } else {
        if (!rejectionReason.trim()) { toast.error('Reason is required'); setSubmitting(false); return }
        await userApprovalApi.reject(action.user.id, rejectionReason.trim())
        toast.success(`Rejected ${action.user.email || action.user.login}`)
      }
      close()
      await refresh()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Action failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ background: 'var(--bk-bg)', minHeight: '100%' }}>
      <section className="bk-page-hero">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="bk-page-title">Pending approvals</h1>
            <p className="bk-page-sub">
              New sign-ins are blocked from every protected endpoint until approved.
            </p>
          </div>
          <button type="button" className="bk-btn bk-btn-secondary" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </section>

      <div style={{ padding: '24px 32px 64px', maxWidth: 1100, margin: '0 auto' }}>
        <div className="bk-card">
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--bk-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="bk-display" style={{ fontSize: 16, fontWeight: 600 }}>Awaiting review</div>
              <div style={{ fontSize: 12.5, color: 'var(--bk-muted)', marginTop: 2 }}>
                {loading ? 'Loading…' : `${users.length} ${users.length === 1 ? 'request' : 'requests'}`}
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 24, display: 'grid', gap: 12 }}>
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="bk-skel" style={{ height: 56 }} />)}
            </div>
          ) : users.length === 0 ? (
            <div className="bk-empty">
              <div className="icon"><UserCheck size={22} /></div>
              <div className="ttl">All caught up</div>
              <div className="sub">No pending users right now.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="bk-tbl">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th className="bk-hide-mobile">Requested</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.login
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="name-cell">
                            {u.image_url ? (
                              <img src={u.image_url} alt="" className="bk-avatar bk-avatar-md" referrerPolicy="no-referrer" />
                            ) : (
                              <div className={`bk-avatar bk-avatar-md ${avatarColorClass(name)}`}>{initials(name)}</div>
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div className="name">{name}</div>
                              <div className="email bk-mono">{u.login}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ color: 'var(--bk-ink-2)' }}>{u.email || '—'}</td>
                        <td className="bk-hide-mobile" style={{ color: 'var(--bk-muted)', fontSize: 13 }}>
                          {u.requested_at ? formatDateTime(u.requested_at) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button type="button" className="bk-btn bk-btn-sm" style={{ background: 'var(--bk-success-soft)', color: 'var(--bk-success)' }} onClick={() => openApprove(u)}>
                              <Check size={14} /> Approve
                            </button>
                            <button type="button" className="bk-btn bk-btn-danger bk-btn-sm" onClick={() => openReject(u)}>
                              <X size={14} /> Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {action && (
        <div className="bk-modal-backdrop" onClick={close}>
          <div className="bk-modal" onClick={e => e.stopPropagation()}>
            <header className="bk-modal-hd">
              <div className="ttl">
                {action.kind === 'approve'
                  ? `Approve ${action.user.email || action.user.login}`
                  : `Reject ${action.user.email || action.user.login}`}
              </div>
              <button className="bk-icon-btn" onClick={close} aria-label="Close"><X size={16} /></button>
            </header>
            <div className="bk-modal-bd">
              {action.kind === 'approve' ? (
                <>
                  <p style={{ fontSize: 13.5, color: 'var(--bk-ink-2)', marginBottom: 12 }}>
                    Pick one or more roles. The user will receive an email confirming access.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                    {authorities.map(a => {
                      const checked = selectedAuthorities.includes(a)
                      return (
                        <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 0' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setSelectedAuthorities(prev =>
                              checked ? prev.filter(x => x !== a) : [...prev, a],
                            )}
                          />
                          <span className="bk-mono" style={{ fontSize: 13 }}>{a}</span>
                        </label>
                      )
                    })}
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 13.5, color: 'var(--bk-ink-2)', marginBottom: 12 }}>
                    The reason will be emailed to the user and shown on their status screen.
                  </p>
                  <textarea
                    className="bk-textarea"
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="e.g. Not a member of this organisation"
                  />
                </>
              )}
            </div>
            <footer className="bk-modal-ft">
              <button className="bk-btn bk-btn-ghost" onClick={close} disabled={submitting}>Cancel</button>
              <button
                className={action.kind === 'approve' ? 'bk-btn bk-btn-primary' : 'bk-btn bk-btn-danger'}
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : action.kind === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
